import React, { useState } from "react";
import { Check } from "lucide-react";
import Switch from "@mui/material/Switch";
import Checkbox from "@mui/material/Checkbox";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import TextareaAutosize from "@mui/material/TextareaAutosize";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";

const ColumnSelector = ({
  columns,
  selectedColumns,
  onToggleColumn,
  onSelectAll,
  columnDetails,
  onCdeChange,
  onDescriptionChange,
  onMetadataChange,
  onTransformationLogicChange,
}) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [currentColumnId, setCurrentColumnId] = useState(null);
  const [tempTransformationLogic, setTempTransformationLogic] = useState("");

  const allSelected = selectedColumns.length === columns.length && columns.length > 0;
  const isIndeterminate = selectedColumns.length > 0 && selectedColumns.length < columns.length;

  const handleOpenDialog = (columnId) => {
    setCurrentColumnId(columnId);
    setTempTransformationLogic(columnDetails[columnId]?.transformationLogic || "");
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentColumnId(null);
    setTempTransformationLogic("");
  };

  const handleSaveTransformationLogic = () => {
    if (currentColumnId !== null) {
      onTransformationLogicChange(currentColumnId, tempTransformationLogic);
    }
    handleCloseDialog();
  };

  return (
    <TableContainer component={Paper} sx={{ 
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      overflow: 'hidden'
    }}>
      <Table size="small" sx={{ minWidth: 650 }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f8fafc' }}>
            <TableCell padding="checkbox" sx={{ 
              borderBottom: '2px solid #e5e7eb',
              backgroundColor: '#f8fafc'
            }}>
              <Checkbox
                indeterminate={isIndeterminate}
                checked={allSelected}
                onChange={onSelectAll}
                inputProps={{ 'aria-label': 'select all columns' }}
                sx={{
                  color: '#ff32a0',
                  '&.Mui-checked': {
                    color: '#ff32a0',
                  },
                  '&.MuiCheckbox-indeterminate': {
                    color: '#ff32a0',
                  }
                }}
              />
            </TableCell>
            <TableCell sx={{
              fontWeight: "600", 
              fontSize: '14px', 
              color: "#ff32a0",
              borderBottom: '2px solid #e5e7eb',
              backgroundColor: '#f8fafc',
              padding: '16px 8px'
            }}>
              CDE
            </TableCell>
            <TableCell sx={{
              fontWeight: "600", 
              fontSize: '14px', 
              color: "#ff32a0",
              borderBottom: '2px solid #e5e7eb',
              backgroundColor: '#f8fafc',
              padding: '16px 8px'
            }}>
              Select Column
            </TableCell>
            <TableCell sx={{
              fontWeight: "600", 
              fontSize: '14px', 
              color: "#ff32a0",
              borderBottom: '2px solid #e5e7eb',
              backgroundColor: '#f8fafc',
              padding: '16px 8px'
            }}>
              Metadata
            </TableCell>
            <TableCell sx={{
              fontWeight: "600", 
              fontSize: '14px', 
              color: "#ff32a0",
              borderBottom: '2px solid #e5e7eb',
              backgroundColor: '#f8fafc',
              padding: '16px 8px'
            }}>
              Business Description
            </TableCell>
            <TableCell sx={{
              fontWeight: "600", 
              fontSize: '14px', 
              color: "#ff32a0",
              borderBottom: '2px solid #e5e7eb',
              backgroundColor: '#f8fafc',
              padding: '16px 8px'
            }}>
              Transformation Logic
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {columns.map((column, index) => {
            const isSelected = selectedColumns.includes(column.id);
            return (
              <TableRow 
                key={column.id} 
                hover 
                selected={isSelected}
                sx={{
                  backgroundColor: isSelected ? '#f0f8ff' : 'white',
                  '&:hover': {
                    backgroundColor: isSelected ? '#e6f3ff' : '#f9fafb'
                  },
                  '&:nth-of-type(even)': {
                    backgroundColor: isSelected ? '#f0f8ff' : '#fafbfc'
                  },
                  transition: 'background-color 0.2s ease'
                }}
              >
                <TableCell padding="checkbox" sx={{ 
                  borderBottom: '1px solid #f3f4f6',
                  padding: '12px 8px'
                }}>
                  <Checkbox
                    checked={isSelected}
                    onChange={() => onToggleColumn(column.id)}
                    inputProps={{ 'aria-label': `select column ${column.name}` }}
                    sx={{
                      color: '#bb1ccc',
                      '&.Mui-checked': {
                        color: '#bb1ccc',
                      }
                    }}
                  />
                </TableCell>
                <TableCell sx={{ 
                  borderBottom: '1px solid #f3f4f6',
                  padding: '12px 8px'
                }}>
                  <Switch
                    checked={columnDetails[column.id]?.cde || false}
                    onChange={(e) => onCdeChange(column.id, e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#ff9800',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 152, 0, 0.08)',
                        },
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#ff9800',
                      },
                    }}
                    disabled={!isSelected}
                  />
                </TableCell>
                <TableCell sx={{ 
                  borderBottom: '1px solid #f3f4f6',
                  padding: '12px 8px'
                }}>
                  <div className="flex flex-col">
                    <span className="font-medium text-gray-900 text-sm">{column.name}</span>
                    <span className="text-xs text-gray-500 mt-1">({column.type})</span>
                  </div>
                </TableCell>
                <TableCell sx={{ 
                  borderBottom: '1px solid #f3f4f6',
                  padding: '12px 8px'
                }}>             
                    <span className="font-medium text-gray-900 text-sm">{column.metadata}</span>
                </TableCell>
                <TableCell sx={{ 
                  borderBottom: '1px solid #f3f4f6',
                  padding: '12px 8px'
                }}>
                  <TextField
                    size="small"
                    fullWidth
                    variant="outlined"
                    placeholder="Enter business description..."
                    value={columnDetails[column.id]?.description || ""}
                    onChange={(e) => onDescriptionChange(column.id, e.target.value)}
                    disabled={!isSelected}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                        '&.Mui-focused fieldset': {
                          borderColor: '#bb1ccc',
                          borderWidth: '2px'
                        },
                        '&:hover fieldset': {
                          borderColor: '#bb1ccc'
                        },
                        '&.Mui-disabled': {
                          backgroundColor: '#f9fafb'
                        }
                      }
                    }}
                  />
                </TableCell>
                <TableCell sx={{ 
                  borderBottom: '1px solid #f3f4f6',
                  padding: '12px 8px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <TextField
                      size="small"
                      fullWidth
                      variant="outlined"
                      placeholder="Enter transformation logic..."
                      value={columnDetails[column.id]?.transformationLogic || ""}
                      onChange={(e) => onTransformationLogicChange(column.id, e.target.value)}
                      disabled={!isSelected}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '6px',
                          '&.Mui-focused fieldset': {
                            borderColor: '#bb1ccc',
                            borderWidth: '2px'
                          },
                          '&:hover fieldset': {
                            borderColor: '#bb1ccc'
                          },
                          '&.Mui-disabled': {
                            backgroundColor: '#f9fafb',
                            cursor: 'not-allowed'
                          }
                        }
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(column.id)}
                      disabled={!isSelected}
                      sx={{
                        color: '#bb1ccc',
                        '&:hover': {
                          backgroundColor: 'rgba(0,0,160,0.08)'
                        },
                        '&.Mui-disabled': {
                          color: '#9ca3af'
                        }
                      }}
                      title="Open full editor"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      
      {/* Transformation Logic Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          backgroundColor: '#f8fafc',
          borderBottom: '1px solid #e5e7eb',
          padding: '16px 24px'
        }}>
          <span style={{ fontWeight: 600, color: '#bb1ccc', fontSize: '18px' }}>
            Transformation Logic
          </span>
          <IconButton
            onClick={handleCloseDialog}
            size="small"
            sx={{
              color: '#6b7280',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.04)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: '24px' }}>
          <TextareaAutosize
            minRows={10}
            maxRows={20}
            value={tempTransformationLogic}
            onChange={(e) => setTempTransformationLogic(e.target.value)}
            placeholder="Enter transformation logic here..."
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '8px',
              border: '1px solid #d1d5db',
              fontSize: '14px',
              fontFamily: 'inherit',
              resize: 'vertical',
              outline: 'none',
              transition: 'border-color 0.2s'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#bb1ccc';
              e.target.style.borderWidth = '2px';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#d1d5db';
              e.target.style.borderWidth = '1px';
            }}
          />
        </DialogContent>
        <DialogActions sx={{ 
          padding: '16px 24px',
          borderTop: '1px solid #e5e7eb',
          backgroundColor: '#f8fafc'
        }}>
          <Button
            onClick={handleCloseDialog}
            sx={{
              color: '#6b7280',
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.04)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveTransformationLogic}
            variant="contained"
            sx={{
              backgroundColor: '#bb1ccc',
              color: '#fff',
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: '6px',
              padding: '8px 24px',
              '&:hover': {
                backgroundColor: '#000080'
              }
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </TableContainer>
  );
};

export default ColumnSelector;
